import { createFileRoute, Link } from "@tanstack/react-router";
import { Suspense, lazy } from "react";
import { Layout } from "@/components/Layout";
import { ratesQueryOptions } from "@/components/CountryPage";
import { SITE_URL } from "@/lib/seo";
import { withTodayDate } from "@/lib/dateIntent";
import { buildToolSchemas } from "@/lib/tools-schema";

const ROICalculator = lazy(() =>
  import("@/components/GoldToolsSuite").then((m) => ({ default: m.ROICalculator })),
);

export const Route = createFileRoute("/tools/historical-roi-calculator")({
  head: () => ({
    meta: [
      { title: withTodayDate("Gold Investment ROI Calculator — 1, 5 & 10 Year Returns") },
      {
        name: "description",
        content:
          "Back-test gold as an investment. See how much PKR / INR invested 1, 5 or 10 years ago is worth at today's live rate, vs KSE-100 stocks and inflation. Free, no signup.",
      },
      { property: "og:title", content: "Historical Gold ROI Calculator — Stocks vs Inflation Comparison" },
      { property: "og:description", content: "Back-test gold returns vs stocks and inflation over 1, 5 and 10 years." },
      { property: "og:url", content: `${SITE_URL}/tools/historical-roi-calculator` },
    ],
    links: [{ rel: "canonical", href: `${SITE_URL}/tools/historical-roi-calculator` }],
    scripts: buildToolSchemas({
      name: "Historical Gold ROI Calculator",
      description: "Back-test gold investment ROI vs stocks and inflation over 1, 5 and 10 years.",
      path: "/tools/historical-roi-calculator",
      steps: [
        { name: "Enter starting amount", text: "Type the PKR or INR amount you would have invested in gold." },
        { name: "Pick a start year", text: "Choose 1, 5 or 10 years ago as the buy date." },
        { name: "Read live return", text: "See today's value at the live spot rate vs KSE-100 stocks and CPI inflation." },
      ],
    }),
  }),
  loader: ({ context }) => {
    void context.queryClient.prefetchQuery(ratesQueryOptions);
  },
  component: Page,
});

function Page() {
  return (
    <Layout>
      <section className="mx-auto max-w-6xl px-4 pt-10 pb-2">
        <nav className="text-xs text-muted-foreground mb-3">
          <Link to="/tools" className="hover:text-gold">Tools</Link> / Historical ROI Calculator
        </nav>
        <h1 className="text-3xl md:text-4xl font-black tracking-tight">Historical Gold ROI Calculator</h1>
        <p className="mt-3 text-muted-foreground max-w-3xl">
          See what your money would be worth today if you had bought gold 1, 5 or 10 years ago — and how
          that return compares to stocks (KSE-100 ~12%) and inflation (~8%).
        </p>
      </section>
      <Suspense fallback={<div className="py-12 text-center text-sm text-muted-foreground">Loading…</div>}>
        <ROICalculator />
      </Suspense>
    </Layout>
  );
}